import type { IHomeObject } from '../shared/home-object';

export const HomeSystemTypeConst = {
  Water: 'water',
  Heating: 'heating',
  Cooling: 'cooling',
  CoolingHeating: 'cooling_heating',
  Hvac: 'hvac',
  Electrical: 'electrical',
  Plumbing: 'plumbing',
  Security: 'security',
  Sewage: 'sewage',
  Waste: 'waste',
  Other: 'other',
} as const;

export type HomeSystemType = (typeof HomeSystemTypeConst)[keyof typeof HomeSystemTypeConst];

export type SystemCondition = 'new' | 'good' | 'fair' | 'poor' | 'unknown';

export interface IHomeSystemConfigBase {
  isPresent: boolean; // Whether the system exists in the home
  yearInstalled?: number;
  yearUpdated?: number;
  lastInspectedDate?: number;
  lastServiced?: number;
  condition?: SystemCondition;
  notes?: string;
  brandID?: string | null;
  model?: string;
}
//condition?: string; to enum

// coverage?: 'Whole House' | 'Partial' | 'Room-Based'; to enum
export type HeatingOrCoolingCoverage = 'whole_house' | 'partial' | 'room_based' | 'other';

export const HeatingOrCoolingCoverage = {
  WHOLE_HOUSE: 'whole_house',
  PARTIAL: 'partial',
  ROOM_BASED: 'room_based',
  OTHER: 'other',
} as const;

//Cooling
export type CoolingType =
  | 'central_ac'
  | 'ductless_mini_split'
  | 'window_unit'
  | 'portable'
  | 'heat_pump'
  | 'none'
  | 'other';

export const CoolingType = {
  CENTRAL_AC: 'central_ac',
  DUCTLESS_MINI_SPLIT: 'ductless_mini_split',
  WINDOW_UNIT: 'window_unit',
  PORTABLE: 'portable',
  HEAT_PUMP: 'heat_pump',
  NONE: 'none',
  OTHER: 'other',
} as const;

export type CoolingFuelSource = 'electricity' | 'geothermal' | 'heat_pump' | 'other';

export const CoolingFuelSource = {
  ELECTRICITY: 'electricity',
  GEOTHERMAL: 'geothermal',
  HEAT_PUMP: 'heat_pump',
  OTHER: 'other',
} as const;

export interface ICoolingSystemConfig extends IHomeSystemConfigBase {
  type: CoolingType;
  fuelSource: CoolingFuelSource;
  coverage?: HeatingOrCoolingCoverage;
  notes?: string;
}

//Heating
export type HeatingType =
  | 'furnace'
  | 'boiler'
  | 'radiator'
  | 'baseboard'
  | 'heat_pump'
  | 'fireplace'
  | 'stove'
  | 'other';

export const HeatingType = {
  FURNACE: 'furnace',
  BOILER: 'boiler',
  RADIATOR: 'radiator',
  BASEBOARD: 'baseboard',
  HEAT_PUMP: 'heat_pump',
  FIREPLACE: 'fireplace',
  STOVE: 'stove',
  OTHER: 'other',
} as const;

export type HeatingFuelSource =
  | 'electricity'
  | 'natural_gas'
  | 'propane'
  | 'geothermal'
  | 'heat_pump'
  | 'oil'
  | 'wood'
  | 'solar'
  | 'other';

export const HeatingFuelSource = {
  ELECTRICITY: 'electricity',
  NATURAL_GAS: 'natural_gas',
  PROPANE: 'propane',
  GEOTHERMAL: 'geothermal',
  HEAT_PUMP: 'heat_pump',
  OIL: 'oil',
  WOOD: 'wood',
  SOLAR: 'solar',
  OTHER: 'other',
} as const;
export interface IHeatingSystemConfig extends IHomeSystemConfigBase {
  type: HeatingType;
  fuelSource: HeatingFuelSource;
  coverage?: HeatingOrCoolingCoverage;
}

// HVAC Systems
export type HvacSystemType =
  | 'split_system'
  | 'hybrid_split_system'
  | 'ductless_mini_split'
  | 'packaged_system'
  | 'geothermal_heat_pump'
  | 'boiler'
  | 'furnace'
  | 'wood_biomass_stove';

export const HvacSystemType = {
  SPLIT_SYSTEM: 'split_system',
  HYBRID_SPLIT_SYSTEM: 'hybrid_split_system',
  DUCTLESS_MINI_SPLIT: 'ductless_mini_split',
  PACKAGED_SYSTEM: 'packaged_system',
  GEOTHERMAL_HEAT_PUMP: 'geothermal_heat_pump',
  BOILER: 'boiler',
  FURNACE: 'furnace',
  WOOD_BIOMASS_STOVE: 'wood_biomass_stove',
} as const;

export type HvacFuelSource =
  | 'natural_gas'
  | 'propane'
  | 'electricity'
  | 'heating_oil'
  | 'wood_biomass'
  | 'geothermal'
  | 'solar'
  | 'other';

export const HvacFuelSource = {
  NATURAL_GAS: 'natural_gas',
  PROPANE: 'propane',
  ELECTRICITY: 'electricity',
  HEATING_OIL: 'heating_oil',
  WOOD_BIOMASS: 'wood_biomass',
  GEOTHERMAL: 'geothermal',
  SOLAR: 'solar',
  OTHER: 'other',
} as const;

export interface IHvacSystemConfig extends IHomeSystemConfigBase {
  type: HvacSystemType;
  fuelSource: HvacFuelSource;
  coverage?: HeatingOrCoolingCoverage;
}

//Water
export type WaterSource = 'municipal' | 'well' | 'cistern' | 'shared' | 'other';

export const WaterSource = {
  MUNICIPAL: 'municipal',
  WELL: 'well',
  CISTERN: 'cistern',
  SHARED: 'shared',
  OTHER: 'other',
} as const;

export type WaterHeaterType = 'tank' | 'tankless' | 'solar' | 'other';

export const WaterHeaterType = {
  TANK: 'tank',
  TANKLESS: 'tankless',
  SOLAR: 'solar',
  OTHER: 'other',
} as const;

export type WaterHeaterFuelSource = 'electricity' | 'natural_gas' | 'propane' | 'solar' | 'other';

export const WaterHeaterFuelSource = {
  ELECTRICITY: 'electricity',
  NATURAL_GAS: 'natural_gas',
  PROPANE: 'propane',
  SOLAR: 'solar',
  OTHER: 'other',
} as const;

export interface IWaterSystemConfig extends IHomeSystemConfigBase {
  source: WaterSource;
  waterHeaterType?: WaterHeaterType;
  waterHeaterFuel?: WaterHeaterFuelSource;
}

//Waste

export type WastePickupLocation = 'curbside' | 'back_alley' | 'doorstep' | 'community_bin' | 'drop_off' | 'other';

export const WastePickupLocation = {
  CURBSIDE: 'curbside',
  BACK_ALLEY: 'back_alley',
  DOORSTEP: 'doorstep',
  COMMUNITY_BIN: 'community_bin',
  DROP_OFF: 'drop_off',
  OTHER: 'other',
} as const;
export interface IWasteSystemConfig extends IHomeSystemConfigBase {
  pickupDays?: string; // e.g., "Tuesday & Friday"
  includesRecycling?: boolean;
  includesCompost?: boolean;
  location?: WastePickupLocation;
  bulkPickupInfo?: string; // e.g., "First Monday of each month"
}

//Sewage
export type SewageType = 'city' | 'septic' | 'holding_tank' | 'composting' | 'other';

export const SewageType = {
  CITY: 'city',
  SEPTIC: 'septic',
  HOLDING_TANK: 'holding_tank',
  COMPOSTING: 'composting',
  OTHER: 'other',
} as const;

export type SepticMaintenanceFrequency = 'annually' | 'bi_annually' | 'every_three_years' | 'as_needed';

export const SepticMaintenanceFrequency = {
  ANNUALLY: 'annually',
  BI_ANNUALLY: 'bi_annually',
  EVERY_THREE_YEARS: 'every_three_years',
  AS_NEEDED: 'as_needed',
} as const;
export interface ISewageSystemConfig extends IHomeSystemConfigBase {
  type: SewageType;
  lastPumpedDate?: number; // Only relevant for Septic systems
  maintenanceFrequency?: SepticMaintenanceFrequency; // Only relevant for Septic systems
}

//Electricity
export interface IElectricalSystemConfig extends IHomeSystemConfigBase {
  amperage: number; // e.g., 100, 200
  circuitCount?: number;
  subPanels?: number;
}

//Plumbing
export type PlumbingType = 'copper' | 'pex' | 'pvc' | 'cpvc' | 'galvanized' | 'cast_iron' | 'other';

export const PlumbingType = {
  Copper: 'copper',
  PEX: 'pex',
  PVC: 'pvc',
  CPVC: 'cpvc',
  Galvanized: 'galvanized',
  CastIron: 'cast_iron',
  Other: 'other',
} as const;

export type PipeCondition = 'good' | 'fair' | 'poor';

export const PipeCondition = {
  Good: 'good',
  Fair: 'fair',
  Poor: 'poor',
} as const;
export interface IPlumbingSystemConfig extends IHomeSystemConfigBase {
  type: PlumbingType;
  mainShutoffLocation?: string;
  waterMeterLocation?: string;
  pipeCondition?: PipeCondition;
}

export type SecuritySystemType =
  | 'alarm'
  | 'camera'
  | 'door_lock'
  | 'motion_sensor'
  | 'intercom'
  | 'smart_home'
  | 'monitoring_service'
  | 'other';

export const SecuritySystemType = {
  ALARM: 'alarm',
  CAMERA: 'camera',
  DOOR_LOCK: 'door_lock',
  MOTION_SENSOR: 'motion_sensor',
  INTERCOM: 'intercom',
  SMART_HOME: 'smart_home',
  MONITORING_SERVICE: 'monitoring_service',
  OTHER: 'other',
} as const;

export interface ISecuritySystemConfig extends IHomeSystemConfigBase {
  type: SecuritySystemType;
  monitored?: boolean;
  provider?: string;
  isSmart?: boolean;
  notes?: string;
}

export interface IOtherSystemConfig extends IHomeSystemConfigBase {
  // optional "kind" label if you want
  kind?: string;
}

export type HomeSystemConfiguration =
  | IHeatingSystemConfig
  | ICoolingSystemConfig
  | IHvacSystemConfig
  | IWaterSystemConfig
  | ISewageSystemConfig
  | IWasteSystemConfig
  | IElectricalSystemConfig
  | IPlumbingSystemConfig
  | ISecuritySystemConfig
  | IOtherSystemConfig;

export type SystemConfigurationMap = {
  [HomeSystemTypeConst.Heating]: IHeatingSystemConfig;
  [HomeSystemTypeConst.Cooling]: ICoolingSystemConfig;
  [HomeSystemTypeConst.CoolingHeating]: IHeatingSystemConfig | ICoolingSystemConfig | IHvacSystemConfig;
  [HomeSystemTypeConst.Hvac]: IHvacSystemConfig;
  [HomeSystemTypeConst.Water]: IWaterSystemConfig;
  [HomeSystemTypeConst.Sewage]: ISewageSystemConfig;
  [HomeSystemTypeConst.Waste]: IWasteSystemConfig;
  [HomeSystemTypeConst.Electrical]: IElectricalSystemConfig;
  [HomeSystemTypeConst.Plumbing]: IPlumbingSystemConfig;
  [HomeSystemTypeConst.Security]: ISecuritySystemConfig;
  [HomeSystemTypeConst.Other]: IOtherSystemConfig;
};

export interface IHomeSystem<T extends keyof SystemConfigurationMap = keyof SystemConfigurationMap>
  extends IHomeObject {
  propertyID: string;
  systemType: T;
  configuration: SystemConfigurationMap[T];
}
